//modules
import React from 'react';
import { gsap } from "gsap";
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { CSSRulePlugin } from 'gsap/CSSRulePlugin';
import { NavigationBar } from '../globalComponents/NavigationBar';
import { ProjectsFooter } from '../globalComponents/ProjectsFooter';
import { useImageAnimation } from '../globalComponents/UseImageAnimation';
import { useTextAnimation } from '../globalComponents/UseTextAnimation';
import { UpButton } from '../globalComponents/UpButton';

//styles
import aptia_0 from '../images/aptia_0.webp';
import aptia_1 from '../images/aptia_1.webp';
import aptia_2 from '../images/aptia_2.webp';
import aptia_3 from '../images/aptia_3.webp';
import aptia_4 from '../images/aptia_4.webp';
import aptia_6 from '../images/aptia_6.webp';
import aptia_7 from '../images/aptia_7.webp';
import aptia_8 from '../images/aptia_8.webp';
import aptia_9 from '../images/aptia_9.webp';
import aptia_10 from '../images/aptia_10.webp';
import aptia_11 from '../images/aptia_11.webp';
import aptia_12 from '../images/aptia_12.webp';
import project_ticketsocket from '../images/project_ticketsocket.webp';
import project_vintach from '../images/project_vintach.webp';
import project_disney from '../images/project_disney.webp';
import doldrumdrifters from '../images/doldrumdrifters.webp'

//plugins
gsap.registerPlugin(CSSRulePlugin, ScrollTrigger);


export const DesignSystem = ({ className }) => {

  const StuffData = [ 
    ['Ticketing Ecosystem', 'Product Ideation & UI Design for a ticketing ecosystem.', project_ticketsocket, '/Projects/Ticketing'],
    ['Vintage Marketplace', 'Branding, Product Ideation & UI Design for a second hand clothing marketplace.', project_vintach, '/Projects/Vintach'],
    ['Figma To Code', 'Low code and design system tools to streamline better hand-off processes at Disney.', project_disney, '/Projects/FigmaToCode'],
    ['Other Stuff', "Personal projects covering illustration, code experiments, and more.", doldrumdrifters, '/OtherStuff'],
 ];

   const { containerRefs, imageRefs } = useImageAnimation(13);
   const { textRef, headingRef, subtitleRef, bodyRef } = useTextAnimation();

    return(
    <div>
        <div>
            <NavigationBar />

            <div className="subpage-heading" ref={headingRef}>
                <h2 className="heading-2">
                    Unity Design System.
                </h2>
                <div className="subpage-subtitle">
                <p className="subtitle" ref={(el) => (subtitleRef.current[0] = el)}>Responsabilities</p>
                <p className="subtitle-bold" ref={(el) => (subtitleRef.current[1] = el)}>Design, Definition & Documentation</p>
                </div>
            </div>

            <div className="project-body">
                <h5 className="heading-5" ref={(el) => (bodyRef.current[0] = el)}>/ Context</h5>
                <p className="project-notes body" ref={(el) => (bodyRef.current[1] = el)}>I led the design efforts for a comprehensive design system for Aptia, a benefits administration company. My role has been to address critical challenges within their ecosystem, including design inconsistencies, accessibility limitations, lengthy implementation times, and complex customization processes.</p>
            </div>

            {/* Image 0 */}
            <section className="main">
                <div ref={containerRefs.current[0]} className="container">
                    <div className="img-container">
                        <img className="single-img" ref={imageRefs.current[0]} src={aptia_0} alt="Project Visual 0" />
                    </div>
                </div>
            </section>

            {/* Section Title */}
            <div className="project-body">
                <h3 className="heading-3 section-heading" ref={(el) => (textRef.current[0] = el)}> Discovery. </h3>
            </div>

            {/* Content */}
            <div className="project-body">
                <h5 className="heading-5" ref={(el) => (textRef.current[1] = el)}>/ Interviews</h5>
                <p className="project-notes body" ref={(el) => (textRef.current[2] = el)}>To create a robust design system that meets Aptia's functional needs, the discovery phase played a pivotal role. This phase focused on conducting thorough research to identify critical gaps within Aptia's existing digital ecosystem. To achieve this, we conducted multiple interviews across various departments within the organization, ensuring a comprehensive understanding of their challenges and requirements.</p>
            </div>

            {/* Image 1 */}
            <section className="main">
                <div ref={containerRefs.current[1]} className="container">
                    <div className="img-container">
                        <img className="single-img" ref={imageRefs.current[1]} src={aptia_1} alt="Project Visual 0" />
                    </div>
                </div>
            </section>

            {/* Content */}
            <div className="project-body">
                <h5 className="heading-5" ref={(el) => (textRef.current[3] = el)}>/ Existing Products</h5>
                <p className="project-notes body" ref={(el) => (textRef.current[4] = el)}>In addition to the interviews, auditing Aptia’s existing products was essential to gain a deeper understanding of their design needs. This thorough analysis allowed me to identify specific component requirements, recognize recurring patterns, and highlight inconsistencies in visual design.</p>
            </div>

            {/* Image 2 */}
            <section className="main">
                <div ref={containerRefs.current[2]} className="container">
                    <div className="img-container">
                        <img className="single-img" ref={imageRefs.current[2]} src={aptia_2} alt="Project Visual 0" />
                    </div>
                </div>
            </section>

            {/* Content */}
            <div className="project-body">
                <h5 className="heading-5" ref={(el) => (textRef.current[5] = el)}>/ Findings</h5>
                <div className="project-notes">
                    <p className="body aptia-findings" ref={(el) => (textRef.current[6] = el)}>The discovery phase was key in understanding the organization’s expectations and challenges. We quickly identified major issues a unified system could solve: visual inconsistencies, accessibility gaps, slow implementation, and complex brand customizations. Most importantly, we pinpointed two critical pain points in Aptia’s processes that a design system could directly address: ensuring WCAG-compliant accessibility across the ecosystem and maintaining a cohesive yet flexible brand experience.</p>
                </div>
            </div>

            {/* Content */}
            <div className="project-body">
                <h5 className="heading-5" ref={(el) => (textRef.current[7] = el)}>/ Challenges</h5>
                <p className="project-notes body" ref={(el) => (textRef.current[8] = el)}>While defining the core design token system, we faced a major challenge: the existing brand was not fully accessible, as it was designed for print and social media rather than digital products. However, a complete overhaul wasn’t an option due to its strong recognition among users.

                To bridge this gap, we collaborated with the Head of Marketing to refine key elements. Through iterative testing, we achieved a balance maintaining brand consistency while making necessary adjustments to ensure a11y compliance in digital environments.</p>
            </div>


            {/* Section Title */}
            <div className="project-body">
                <h3 className="heading-3 section-heading" ref={(el) => (textRef.current[9] = el)}> Theming Model. </h3>
            </div>

            {/* Image 3 & 4 */}
            <section className="main row-container large-gap">
                <div ref={containerRefs.current[3]} className="multiple-container">
                    <div className="img-container">
                        <img className="single-img" ref={imageRefs.current[3]} src={aptia_3} alt="Project Visual 2" />
                    </div>
                </div>
                <div ref={containerRefs.current[4]} className="multiple-container">
                    <div className="img-container">
                        <img className="single-img" ref={imageRefs.current[4]} src={aptia_4} alt="Project Visual 3" />
                    </div>
                </div>
            </section>

            {/* Content */}
            <div className="project-body">
                <h5 className="heading-5" ref={(el) => (textRef.current[10] = el)}>/ Customization</h5>
                <p className="project-notes body" ref={(el) => (textRef.current[11] = el)}>As a white-label design system, flexibility was key. To accommodate both fully branded and semi-branded products, I designed a flexible customization framework. This involved creating collections for each customizable property, allowing Aptia to adjust the degree of white labeling based on each product's specific needs.</p>
            </div>

            {/* Image 6 */}
            <section className="main">
                <div ref={containerRefs.current[6]} className="container">
                    <div className="img-container">
                        <img className="single-img" ref={imageRefs.current[6]} src={aptia_6} alt="Project Visual 0" />
                    </div>
                </div>
            </section>

            {/* Section Title */}
            <div className="project-body">
                <h3 className="heading-3 section-heading" ref={(el) => (textRef.current[12] = el)}> Components. </h3>
            </div>

            {/* Content */}
            <div className="project-body">
                <h5 className="heading-5" ref={(el) => (textRef.current[13] = el)}>/ Pattern Approach</h5>
                <p className="project-notes body" ref={(el) => (textRef.current[14] = el)}>To ensure meaningful progress in the design system, we adopted a pattern-oriented approach. Rather than building components strictly from atoms to organisms, we first identified the most common patterns across the Aptia ecosystem. This allowed us to prioritize and develop the specific components needed to support these patterns, ensuring practical alignment with real use cases and immediate impact on the overall design consistency.</p>
            </div>

            {/* Image 7 */}
            <section className="main">
                <div ref={containerRefs.current[7]} className="container">
                    <div className="img-container">
                        <img className="single-img" ref={imageRefs.current[7]} src={aptia_7} alt="Project Visual 0" />
                    </div>
                </div>
            </section>

            {/* Image 8 */}
            <section className="main">
                <div ref={containerRefs.current[8]} className="container">
                    <div className="img-container">
                        <img className="single-img" ref={imageRefs.current[8]} src={aptia_8} alt="Project Visual 0" />
                    </div>
                </div>
            </section>

            {/* Section Title */}
            <div className="project-body">
                <h3 className="heading-3 section-heading" ref={(el) => (textRef.current[15] = el)}> Documentation. </h3>
            </div>

            {/* Content */}
            <div className="project-body">
                <h5 className="heading-5" ref={(el) => (textRef.current[16] = el)}>/ Construction</h5>
                <p className="project-notes body" ref={(el) => (textRef.current[17] = el)}>Thorough documentation of each component’s anatomy and structure was essential for seamless implementation. By detailing the scope, variants, boolean options, and other configurations, it was ensured that both developers and designers could fully understand and utilize each component, facilitating efficient collaboration and consistency across the platform.</p>
            </div>

            {/* Image 9 */}
            <section className="main">
                <div ref={containerRefs.current[9]} className="container">
                    <div className="img-container">
                        <img className="single-img" ref={imageRefs.current[9]} src={aptia_9} alt="Project Visual 11" />
                    </div>
                </div>
            </section>

            {/* Content */}
            <div className="project-body">
                <h5 className="heading-5" ref={(el) => (textRef.current[18] = el)}>/ Best Practices</h5>
                <p className="project-notes body" ref={(el) => (textRef.current[19] = el)}>If components are the ingredients, then documentation serves as very much needed recipe. Providing designers with clear resources and guidelines was essential to help them create accessible, high-quality user experiences.</p>
            </div>

            {/* Image 10 */}
            <section className="main">
                <div ref={containerRefs.current[10]} className="container">
                    <div className="img-container">
                        <img className="single-img" ref={imageRefs.current[10]} src={aptia_10} alt="Project Visual 11" />
                    </div>
                </div>
            </section>

            {/* Section Title */}
            <div className="project-body">
                <h3 className="heading-3 section-heading" ref={(el) => (textRef.current[20] = el)}> Governance. </h3>
            </div>

            {/* Content */}
            <div className="project-body">
                <h5 className="heading-5" ref={(el) => (textRef.current[21] = el)}>/ Versioning</h5>
                <p className="project-notes body" ref={(el) => (textRef.current[22] = el)}>To promote effective collaboration and clear communication among team members and design system adopters, a Semantic Versioning approach was implemented to track design changes. By leveraging Figma’s branching feature, updates and additions to the design system were only released and published after receiving approval and commitment from the engineering team.</p>
            </div>

            {/* Image 11 */}
            <section className="main">
                <div ref={containerRefs.current[11]} className="container">
                    <div className="img-container">
                        <img className="single-img" ref={imageRefs.current[11]} src={aptia_11} alt="Project Visual 11" />
                    </div>
                </div>
            </section>

            {/* Content */}
            <div className="project-body">
                <h5 className="heading-5" ref={(el) => (textRef.current[23] = el)}>/ Contributions</h5>
                <p className="project-notes body" ref={(el) => (textRef.current[24] = el)}>Encouraging active participation was key to the evolution of the design system. A well-defined contribution process allowed team members to propose new components, patterns, or updates while adhering to established design and development standards. Each proposal underwent a thorough review by design and engineering teams to ensure it aligned with the system’s principles and supported its long-term scalability.</p>
            </div>

            {/* Image 12 */}
            <section className="main">
                <div ref={containerRefs.current[12]} className="container">
                    <div className="img-container">
                        <img className="single-img" ref={imageRefs.current[12]} src={aptia_12} alt="Project Visual 11" />
                    </div>
                </div>
            </section>

            {/* Content */}
            <div className="last-body">
                <h5 className="heading-5" ref={(el) => (textRef.current[25] = el)}>/ Takeaways</h5>
                <p className="project-notes body" ref={(el) => (textRef.current[26] = el)}>Leading the development of the Aptia design system provided invaluable experience in establishing processes that enhance collaboration and promote design standards across an organization. This initiative not only achieved greater consistency but also introduced accessibility criteria, addressing a critical gap that had previously been overlooked.</p>
            </div>
         <ProjectsFooter StuffData={StuffData} />
        </div>
        <UpButton />
    </div>
    )
};